import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import PosterCard from "@/components/feature/PosterCard";
import { applyFilter, defaultFilter, searchTitles, titles, type FilterState } from "@/lib/catalog";

const quickKeywords = ["悬疑", "爱情", "治愈", "韩国", "日本", "美食", "2026"];

const yearOptions = ["全部", "2026", "2025", "2024"];
const regionOptions = ["全部", "日本", "韩国"];
const sortOptions = ["最新", "最热", "评分"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);
  const [filter, setFilter] = useState<FilterState>(defaultFilter);

  useEffect(() => {
    setInput(q);
  }, [q]);

  const matched = useMemo(() => (q.trim() ? searchTitles(q, titles) : []), [q]);
  const results = useMemo(() => applyFilter(matched, filter), [matched, filter]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    setSearchParams(next ? { q: next } : {}, { replace: true });
  };

  const hasQuery = q.trim().length > 0;

  return (
    <>
      <PageMeta
        title={`${hasQuery ? q : "搜索"} - 在线观看日韩影视作品 -日韩在线`}
        description={
          hasQuery
            ? `搜索「${q}」共找到 ${results.length} 部相关日韩影视作品，可按年份、地区与热度进一步筛选，快速找到想看的电影或电视剧。`
            : "在日韩在线搜索日韩电影与电视剧，支持按影视名称、主演、导演与题材模糊匹配。"
        }
        keywords={`${q ? `${q},` : ""}日韩影视搜索,日韩电影,日韩电视剧,在线观看日韩影视`}
      />
      <SiteLayout topOffset>
        <section className="w-full bg-primary-950">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <nav className="flex items-center gap-1.5 text-xs text-background-200/70 flex-wrap" aria-label="面包屑">
              <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center" />
              <span className="text-background-50">搜索</span>
            </nav>

            <h1 className="mt-5 font-heading text-2xl md:text-3xl font-black text-background-50 tracking-tight">
              {hasQuery ? (
                <>搜索「<span className="text-accent-400">{q}</span>」</>
              ) : (
                "搜索日韩影视"
              )}
            </h1>

            <form onSubmit={submit} className="mt-5 flex items-center gap-2 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base w-4 h-4 flex items-center justify-center" />
                <input
                  type="search"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入影视名称、主演或导演关键词"
                  aria-label="搜索关键词"
                  className="w-full h-12 pl-11 pr-4 rounded-md bg-background-50 border border-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 transition-shadow"
                />
              </div>
              <button
                type="submit"
                className="h-12 px-5 md:px-6 rounded-md bg-accent-500 text-background-50 text-sm font-label font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-label text-background-200/60">热门搜索</span>
              {quickKeywords.map((kw) => (
                <Link
                  key={kw}
                  to={`/search?q=${encodeURIComponent(kw)}`}
                  className="px-2.5 py-1 rounded-full bg-background-50/10 border border-background-50/15 text-[11px] font-label text-background-100 hover:bg-background-50/20 transition-colors"
                >
                  {kw}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-foreground-800">
                共找到 <span className="font-label font-bold text-accent-700 text-lg">{results.length}</span> 部相关作品
              </p>
              <p className="mt-1 text-xs text-foreground-500">
                {hasQuery ? "匹配范围为影视名称、原名、主演、导演与题材" : "输入关键词后开始搜索，支持模糊匹配"}
              </p>
            </div>
            {hasQuery && (
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70 overflow-x-auto no-scrollbar">
                {sortOptions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFilter((prev) => ({ ...prev, sort: s }))}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-label font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                      filter.sort === s ? "bg-primary-600 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {s === "最新" ? "最新" : s === "最热" ? "最热" : "评分"}
                  </button>
                ))}
              </div>
            )}
          </div>

          {hasQuery && (
            <div className="mt-4 rounded-lg bg-background-50 border border-background-200/80 p-4 flex flex-col gap-3">
              {[{ key: "year" as const, label: "年份", options: yearOptions }, { key: "region" as const, label: "地区", options: regionOptions }].map((row) => (
                <div key={row.key} className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-label font-semibold text-foreground-500 w-10">{row.label}</span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {row.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setFilter((prev) => ({ ...prev, [row.key]: opt }))}
                        className={`px-3 py-1 rounded-full text-xs font-label whitespace-nowrap cursor-pointer transition-colors border ${
                          filter[row.key] === opt
                            ? "bg-primary-600 border-primary-600 text-background-50"
                            : "bg-background-50 border-background-200 text-foreground-700 hover:border-background-300"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!hasQuery ? (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mx-auto">
                <i className="ri-search-line text-2xl w-6 h-6 flex items-center justify-center" />
              </span>
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-900">还没有输入关键词</h2>
              <p className="mt-1.5 text-xs text-foreground-500">试试搜索「韩剧」「悬疑」或者演员名字。</p>
            </div>
          ) : results.length === 0 ? (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center px-4">
              <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mx-auto">
                <i className="ri-emotion-unhappy-line text-2xl w-6 h-6 flex items-center justify-center" />
              </span>
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-900">
                未找到相关影视内容，请尝试其他关键词
              </h2>
              <p className="mt-1.5 text-xs text-foreground-500">
                可以换个片名、演员，或放宽年份与地区筛选条件。
              </p>
              <Link
                to="/"
                className="mt-5 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-600 text-background-50 text-xs font-label font-semibold whitespace-nowrap hover:bg-primary-700 transition-colors"
              >
                <i className="ri-home-4-line w-3.5 h-3.5 flex items-center justify-center" />
                返回首页
              </Link>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-7 md:gap-x-5 md:gap-y-9">
              {results.map((item) => (
                <PosterCard key={item.id} item={item} showSynopsis />
              ))}
            </div>
          )}
        </section>
      </SiteLayout>
    </>
  );
}